import { useQuery } from '@tanstack/react-query'
import { Link, useOutletContext } from 'react-router-dom'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { useLiveFeed } from '../lib/live'
import { ago, count, duration, eventLabel, km, kph } from '../lib/format'
import type { FleetEvent, ListResponse, Summary } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { EmptyState, ErrorState, Loading, Metric, Panel, SeverityChip } from '../components/ui'

export function Dashboard() {
  const { user } = useAuth()
  const feed = useOutletContext<ReturnType<typeof useLiveFeed>>()

  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: () => api<Summary>('/summary'),
    refetchInterval: 30_000,
  })

  const alerts = useQuery({
    queryKey: ['events', 'recent'],
    queryFn: () =>
      api<ListResponse<FleetEvent>>(
        `/events${qs({ range: '24h', limit: 8, unacknowledged: true })}`,
      ),
    refetchInterval: 60_000,
  })

  const s = summary.data

  return (
    <Page
      title={greeting(user?.fullName)}
      subtitle="Fleet status at a glance"
      actions={
        <Link className="btn btn-primary" to="/live">
          <Icon name="map" />
          Open live map
        </Link>
      }
    >
      {summary.isLoading ? (
        <Loading label="Reading fleet status" />
      ) : summary.error ? (
        <ErrorState error={summary.error} retry={() => void summary.refetch()} />
      ) : (
        <div className="space-y-4">
          {/* --- state of the fleet, right now --- */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
            <Metric
              label="Moving"
              value={count(s?.moving)}
              tone="moving"
              hint={`of ${count(s?.vehicles)} vehicles`}
              icon={<Icon name="route" size={13} />}
            />
            <Metric
              label="Idling"
              value={count(s?.idle)}
              tone="idle"
              hint="engine on, not moving"
              icon={<Icon name="clock" size={13} />}
            />
            <Metric
              label="Stopped"
              value={count(s?.stopped)}
              tone="stopped"
              hint="parked, reporting"
              icon={<Icon name="pause" size={13} />}
            />
            <Metric
              label="No signal"
              value={count(s?.offline)}
              tone="offline"
              hint={`${count(s?.devicesOnline)} of ${count(s?.devices)} devices online`}
              icon={<Icon name="signal" size={13} />}
            />
          </section>

          <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
            <div className="space-y-4">
              {/* --- today --- */}
              <Panel
                title="Today"
                subtitle="Since midnight, across every vehicle you can see"
                action={
                  <Link className="btn btn-ghost btn-sm" to="/reports">
                    Reports <Icon name="chevron" size={12} />
                  </Link>
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5">
                  <Readout label="Distance" value={km((s?.todayDistanceKm ?? 0) * 1000, 0)} />
                  <Readout label="Trips" value={count(s?.todayTrips)} />
                  <Readout
                    label="Idle time"
                    value={duration((s?.todayIdleMinutes ?? 0) * 60)}
                    tone={idleTone(s)}
                  />
                  <Readout
                    label="Avg per vehicle"
                    value={
                      s && s.vehicles > 0
                        ? km(((s.todayDistanceKm ?? 0) * 1000) / s.vehicles, 1)
                        : '—'
                    }
                  />
                </div>

                {s && s.vehicles > 0 && (
                  <div className="mt-6 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="label">Fleet utilisation right now</span>
                      <span className="tnum text-[12px] text-[var(--color-ink-2)]">
                        {Math.round(((s.moving + s.idle) / s.vehicles) * 100)}% in use
                      </span>
                    </div>
                    <StackedBar summary={s} />
                  </div>
                )}
              </Panel>

              {/* --- live ticker --- */}
              <Panel
                title="Live feed"
                subtitle={
                  feed.status === 'live'
                    ? `${feed.packets} frames received this session`
                    : 'Waiting for telemetry'
                }
                action={
                  <span className="label text-[10px]" style={{ color: 'var(--accent-text)' }}>
                    {feed.rate < 1 ? feed.rate.toFixed(1) : Math.round(feed.rate)} pkt/s
                  </span>
                }
                padded={false}
              >
                {feed.events.length === 0 ? (
                  <EmptyState
                    title="Nothing yet"
                    body="Events raised by the rule engine appear here the moment they happen."
                    icon={<Icon name="pulse" size={22} />}
                  />
                ) : (
                  <ul className="max-h-[280px] overflow-y-auto">
                    {feed.events.slice(0, 12).map((e) => (
                      <li
                        key={e.id}
                        className="flex items-center gap-3 px-3.5 py-2 rule-b last:border-b-0"
                        style={{ animation: 'rise 0.3s var(--ease-glide) both' }}
                      >
                        <SeverityChip severity={e.severity} />
                        <span className="text-[13px] text-[var(--color-ink)] truncate grow">
                          {e.message}
                        </span>
                        <span className="tnum text-[11px] text-[var(--color-ink-4)] shrink-0">
                          {ago(e.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            {/* --- sidebar --- */}
            <div className="space-y-4">
              <Panel
                title="Unacknowledged alerts"
                subtitle="Last 24 hours"
                action={
                  <Link className="btn btn-ghost btn-sm" to="/events">
                    All <Icon name="chevron" size={12} />
                  </Link>
                }
                padded={false}
              >
                {alerts.isLoading ? (
                  <Loading label="Loading" />
                ) : !alerts.data?.items.length ? (
                  <EmptyState
                    title="All clear"
                    body="Nothing needs your attention."
                    icon={<Icon name="shield" size={22} />}
                  />
                ) : (
                  <ul>
                    {alerts.data.items.map((e) => (
                      <li key={e.id} className="px-3.5 py-2.5 rule-b last:border-b-0">
                        <div className="flex items-start gap-2.5">
                          <span
                            className="dot mt-1.5"
                            style={{
                              background:
                                e.severity === 'critical'
                                  ? 'var(--color-critical)'
                                  : e.severity === 'warning'
                                    ? 'var(--color-warn)'
                                    : 'var(--color-ink-4)',
                            }}
                          />
                          <div className="min-w-0 grow">
                            <div className="text-[13px] text-[var(--color-ink)] leading-snug">
                              {e.message ?? eventLabel(e.type)}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="label text-[10px]">{eventLabel(e.type)}</span>
                              <span className="tnum text-[10px] text-[var(--color-ink-4)]">
                                {ago(e.at)}
                              </span>
                            </div>
                          </div>
                          {e.speedKph != null && (
                            <span className="tnum text-[11px] text-[var(--color-ink-3)] shrink-0">
                              {kph(e.speedKph)}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Fleet inventory">
                <dl className="space-y-3">
                  <InventoryRow label="Vehicles" value={count(s?.vehicles)} to="/vehicles" />
                  <InventoryRow label="Drivers" value={count(s?.drivers)} to="/drivers" />
                  <InventoryRow label="Geofences" value={count(s?.geofences)} to="/geofences" />
                  <InventoryRow
                    label="Devices online"
                    value={`${count(s?.devicesOnline)} / ${count(s?.devices)}`}
                    to="/devices"
                    tone={
                      s && s.devices > 0 && s.devicesOnline / s.devices < 0.8 ? 'warn' : undefined
                    }
                  />
                </dl>
              </Panel>
            </div>
          </div>
        </div>
      )}
    </Page>
  )
}

function Readout({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="label mb-1.5">{label}</div>
      <div
        className="tnum text-[1.25rem] font-semibold tracking-tight"
        style={{ color: tone ?? 'var(--color-ink)' }}
      >
        {value}
      </div>
    </div>
  )
}

function InventoryRow({
  label,
  value,
  to,
  tone,
}: {
  label: string
  value: string
  to: string
  tone?: 'warn'
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt>
        <Link
          className="text-[13px] text-[var(--color-ink-2)] hover:text-[var(--accent)] transition-colors"
          to={to}
        >
          {label}
        </Link>
      </dt>
      <dd
        className="tnum text-[13px]"
        style={{ color: tone === 'warn' ? 'var(--text-warn)' : 'var(--color-ink)' }}
      >
        {value}
      </dd>
    </div>
  )
}

/** One bar showing the whole fleet's composition. Reads faster than four
 *  separate percentages, and the widths are the same numbers as the tiles. */
function StackedBar({ summary }: { summary: Summary }) {
  const segments = [
    { key: 'moving', n: summary.moving, color: 'var(--text-moving)', label: 'Moving' },
    { key: 'idle', n: summary.idle, color: 'var(--text-idle)', label: 'Idle' },
    { key: 'stopped', n: summary.stopped, color: 'var(--text-stopped)', label: 'Stopped' },
    { key: 'offline', n: summary.offline, color: 'var(--color-offline)', label: 'No signal' },
  ].filter((seg) => seg.n > 0)

  const total = segments.reduce((sum, seg) => sum + seg.n, 0) || 1

  return (
    <div className="space-y-2.5">
      <div
        className="h-2 w-full flex rounded-[2px] overflow-hidden"
        style={{ background: 'var(--color-hull)' }}
      >
        {segments.map((seg) => (
          <div
            key={seg.key}
            className="h-full transition-[width] duration-500"
            style={{ width: `${(seg.n / total) * 100}%`, background: seg.color }}
            title={`${seg.label}: ${seg.n}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {segments.map((seg) => (
          <span key={seg.key} className="flex items-center gap-1.5">
            <span className="dot" style={{ background: seg.color }} />
            <span className="label text-[10px]">{seg.label}</span>
            <span className="tnum text-[11px] text-[var(--color-ink-2)]">{seg.n}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

function idleTone(s: Summary | undefined): string | undefined {
  if (!s || !s.todayTrips) return undefined
  // An hour of idling per trip is the point at which it stops being traffic
  // and starts being a habit worth a conversation.
  return s.todayIdleMinutes / s.todayTrips > 60 ? 'var(--text-warn)' : undefined
}

function greeting(name?: string) {
  const h = new Date().getHours()
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
  const first = name?.split(' ')[0]
  return first ? `${part}, ${first}` : part
}
